import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  MAX_FLIGHT_LENGTH,
  MAX_NAME_LENGTH,
  MAX_NOTES_LENGTH,
  PricingData,
  QuoteInput,
  buildWhatsAppMessage,
  buildWhatsAppUrl,
  calculateQuote,
  clampText,
  onlyDigits,
} from "../lib/pricing";

export interface QuoteEndpointState {
  placeId?: string;
  text: string;
}

export interface QuoteFormState {
  origin: QuoteEndpointState;
  destination: QuoteEndpointState;
  passengers: number;
  roundTrip: boolean;
  /** formato do input datetime-local: YYYY-MM-DDTHH:mm */
  date: string;
  flight: string;
  extraIds: string[];
  name: string;
  phone: string;
  notes: string;
}

export const EMPTY_QUOTE_FORM: QuoteFormState = {
  origin: { text: "" },
  destination: { text: "" },
  passengers: 1,
  roundTrip: false,
  date: "",
  flight: "",
  extraIds: [],
  name: "",
  phone: "",
  notes: "",
};

const readDraft = (key: string): QuoteFormState | null => {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      ...EMPTY_QUOTE_FORM,
      ...parsed,
      origin: { text: "", ...(parsed.origin || {}) },
      destination: { text: "", ...(parsed.destination || {}) },
      extraIds: Array.isArray(parsed.extraIds) ? parsed.extraIds : [],
    };
  } catch {
    return null;
  }
};

const writeDraft = (key: string, state: QuoteFormState) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    // armazenamento indisponível (modo privado, cota); segue sem rascunho
  }
};

interface Options {
  /** chave do sessionStorage para manter o rascunho entre navegações */
  persistKey?: string;
}

export function useQuote(data: PricingData, options: Options = {}) {
  const { persistKey } = options;
  const [state, setState] = useState<QuoteFormState>(() =>
    (persistKey && readDraft(persistKey)) || EMPTY_QUOTE_FORM
  );

  useEffect(() => {
    if (persistKey) writeDraft(persistKey, state);
  }, [persistKey, state]);

  const maxPassengers = Math.max(1, data.config.maxPassengers);

  const setField = useCallback(
    <K extends keyof QuoteFormState>(field: K, value: QuoteFormState[K]) => {
      setState((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const setPassengers = useCallback(
    (value: number) => {
      const next = Math.min(maxPassengers, Math.max(1, Math.floor(value || 1)));
      setState((prev) => ({ ...prev, passengers: next }));
    },
    [maxPassengers]
  );

  const swapEndpoints = useCallback(() => {
    setState((prev) => ({ ...prev, origin: prev.destination, destination: prev.origin }));
  }, []);

  const toggleExtra = useCallback((extraId: string) => {
    setState((prev) => ({
      ...prev,
      extraIds: prev.extraIds.includes(extraId)
        ? prev.extraIds.filter((id) => id !== extraId)
        : [...prev.extraIds, extraId],
    }));
  }, []);

  const reset = useCallback(() => setState(EMPTY_QUOTE_FORM), []);

  const input = useMemo<QuoteInput>(() => {
    const parsedDate = state.date ? dayjs(state.date) : null;
    return {
      originPlaceId: state.origin.placeId,
      originText: clampText(state.origin.text, MAX_NAME_LENGTH),
      destinationPlaceId: state.destination.placeId,
      destinationText: clampText(state.destination.text, MAX_NAME_LENGTH),
      passengers: Math.min(maxPassengers, Math.max(1, state.passengers)),
      roundTrip: state.roundTrip,
      date: parsedDate && parsedDate.isValid() ? parsedDate.valueOf() : undefined,
      flight: clampText(state.flight, MAX_FLIGHT_LENGTH),
      extraIds: state.extraIds,
      name: clampText(state.name, MAX_NAME_LENGTH),
      phone: onlyDigits(state.phone),
      notes: clampText(state.notes, MAX_NOTES_LENGTH),
    };
  }, [state, maxPassengers]);

  const result = useMemo(() => calculateQuote(input, data), [input, data]);

  const hasDestination = Boolean(input.destinationText || input.destinationPlaceId);

  const message = useMemo(() => buildWhatsAppMessage(input, result), [input, result]);
  const whatsappUrl = useMemo(
    () => buildWhatsAppUrl(data.config.whatsappNumber, message),
    [data.config.whatsappNumber, message]
  );

  return {
    state,
    setField,
    setPassengers,
    swapEndpoints,
    toggleExtra,
    reset,
    input,
    result,
    message,
    whatsappUrl,
    canSubmit: hasDestination,
    maxPassengers,
  };
}

export type UseQuoteReturn = ReturnType<typeof useQuote>;
